import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { openPrestationDocument, getIssuer } from '../lib/prestationDoc';

const PAYMENT_METHODS = [
  { value: '', label: '—' },
  { value: 'virement', label: 'Virement' },
  { value: 'cheque', label: 'Chèque' },
  { value: 'carte', label: 'Carte' },
  { value: 'carpa', label: 'CARPA' },
  { value: 'espece', label: 'Espèces' },
  { value: 'autre', label: 'Autre' },
];

const STATUS_TABS = [
  { value: 'all', label: 'Toutes', emoji: '📂' },
  { value: 'brouillon', label: 'À émettre', emoji: '📋' },
  { value: 'emise', label: 'Émises', emoji: '📨' },
  { value: 'en_retard', label: 'En retard', emoji: '⚠️' },
  { value: 'payee', label: 'Payées', emoji: '✅' },
  { value: 'annulee', label: 'Annulées', emoji: '🚫' },
];

function fmtMoney(n) {
  return Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function fmtDate(d) {
  if (!d) return '';
  return new Date(d + 'T00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function emptyForm() {
  const today = new Date().toISOString().split('T')[0];
  const due = new Date();
  due.setDate(due.getDate() + 8);
  return {
    invoice_number: '', dossier: '', client_id: '', description: '',
    amount_ht: '', tva_rate: 21, status: 'brouillon',
    doc_type: 'facture', vat_regime: 'societe_21',
    issue_date: today, due_date: due.toISOString().split('T')[0],
    has_payment_plan: false, paid_amount: 0, paid_date: '',
    payment_method: '', carpa: false, carpa_amount: '', notes: '',
    schedule: [],
  };
}

// Régimes de TVA nommés
const VAT_REGIMES = {
  societe_21:     { label: '🏢 Société — IVA 21 %', rate: 21 },
  intracom_0:     { label: '🏢 Société — TVA intracommunautaire 0 %', rate: 0 },
  particulier_21: { label: '👤 Particulier — IVA 21 %', rate: 21 },
};
const DOC_TYPES = {
  facture:            { label: '📄 Facture', badge: null },
  proforma:           { label: '📋 Facture proforma', badge: { text: 'PROFORMA', bg: '#fffbeb', color: '#b45309' } },
  provision:          { label: '💰 Provision sur honoraires', badge: { text: 'PROVISION', bg: '#eff6ff', color: '#2563eb' } },
  proforma_provision: { label: '📋 Proforma de provision', badge: { text: 'PROFORMA PROV.', bg: '#fffbeb', color: '#b45309' } },
};
const DOC_LABEL_FALLBACK = (t) =>
  (t === 'provision' || t === 'proforma_provision') ? 'Provision sur honoraires' : 'Honoraires';

export default function FacturationPage() {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [dossiers, setDossiers] = useState([]);
  const [stats, setStats] = useState({ to_issue: 0, pending: 0, paid_this_month: 0, paid_this_year: 0, overdue_total: 0, overdue_nb: 0, nb_to_issue: 0, nb_pending: 0 });
  const [activeTab, setActiveTab] = useState('all');
  const [groupBy, setGroupBy] = useState(() => localStorage.getItem('fac_group_by') || 'status');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  useEffect(() => { localStorage.setItem('fac_group_by', groupBy); }, [groupBy]);

  const load = useCallback(async () => {
    try {
      const [invRes, cliRes, dosRes, statsRes] = await Promise.all([
        api.get('/invoices'),
        api.get('/clients'),
        api.get('/tasks/meta/dossiers'),
        api.get('/invoices/stats'),
      ]);
      setInvoices(invRes.data);
      setClients(cliRes.data);
      setDossiers(dosRes.data);
      setStats(statsRes.data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Filtrage selon onglet actif
  const today = new Date().toISOString().split('T')[0];
  const filteredInvoices = invoices.filter(inv => {
    if (activeTab === 'all') return true;
    if (activeTab === 'en_retard') return inv.status === 'emise' && inv.due_date && inv.due_date < today;
    return inv.status === activeTab;
  });

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setShowModal(true);
  };

  const openEdit = async (inv) => {
    setEditingId(inv.id);
    try {
      const r = await api.get(`/invoices/${inv.id}`);
      setForm({
        invoice_number: r.data.invoice_number || '',
        dossier: r.data.dossier || '',
        client_id: r.data.client_id || '',
        description: r.data.description || '',
        amount_ht: r.data.amount_ht || 0,
        tva_rate: r.data.tva_rate || 21,
        status: r.data.status,
        doc_type: r.data.doc_type || 'facture',
        vat_regime: r.data.vat_regime || 'societe_21',
        issue_date: r.data.issue_date || '',
        due_date: r.data.due_date || '',
        has_payment_plan: r.data.has_payment_plan || false,
        paid_amount: r.data.paid_amount || 0,
        paid_date: r.data.paid_date || '',
        payment_method: r.data.payment_method || '',
        carpa: r.data.carpa || false,
        carpa_amount: r.data.carpa_amount || '',
        notes: r.data.notes || '',
        schedule: r.data.schedule || [],
      });
      setShowModal(true);
    } catch {}
  };

  // Auto-suggestion du régime de TVA selon le client sélectionné
  const regimeFromClient = (clientId) => {
    const client = clients.find(c => c.id === parseInt(clientId));
    if (!client) return form.vat_regime;
    if (client.type === 'societe' && client.country !== 'ES') return 'intracom_0';
    if (client.type === 'societe') return 'societe_21';
    return 'particulier_21';
  };

  const handleClientChange = (clientId) => {
    const regime = regimeFromClient(clientId);
    setForm({ ...form, client_id: clientId, vat_regime: regime, tva_rate: VAT_REGIMES[regime].rate });
  };

  const handleRegimeChange = (regime) => {
    setForm({ ...form, vat_regime: regime, tva_rate: VAT_REGIMES[regime] ? VAT_REGIMES[regime].rate : form.tva_rate });
  };

  // Recalcul auto des dates échéance +8j si issue_date change
  const handleIssueDateChange = (newDate) => {
    const due = new Date(newDate);
    due.setDate(due.getDate() + 8);
    setForm({ ...form, issue_date: newDate, due_date: due.toISOString().split('T')[0] });
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      client_id: form.client_id || null,
      dossier: form.dossier || null,
      issue_date: form.issue_date || null,
      due_date: form.due_date || null,
      paid_date: form.paid_date || null,
      carpa_amount: form.carpa_amount || null,
      amount_ht: parseFloat(form.amount_ht) || 0,
      tva_rate: parseFloat(form.tva_rate) || 0,
      paid_amount: parseFloat(form.paid_amount) || 0,
    };
    try {
      if (editingId) await api.put(`/invoices/${editingId}`, payload);
      else await api.post('/invoices', payload);
      setShowModal(false);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const deleteInvoice = async (id) => {
    if (!window.confirm('Supprimer cette facture ?')) return;
    await api.delete(`/invoices/${id}`);
    load();
  };

  // Génère le PDF détaillé (honoraires + frais) d'une facture issue des prestations
  const generateDetail = async (inv) => {
    try {
      const r = await api.get('/prestations', { params: { invoice_id: inv.id } });
      const prestations = r.data || [];
      // Provision / facture manuelle : pas de prestations détaillées → ligne forfaitaire
      const simpleLines = prestations.length === 0
        ? [{ description: inv.description || DOC_LABEL_FALLBACK(inv.doc_type), amount: inv.amount_ht }]
        : [];
      const clientObj = clients.find(c => c.id === inv.client_id) || null;
      let dossierNumbers = {};
      try {
        const dRes = await api.get('/dossiers');
        dossierNumbers = Object.fromEntries((dRes.data || []).filter(d => d.dossier_number).map(d => [d.name, d.dossier_number]));
      } catch { /* ignore */ }
      openPrestationDocument({
        kind: 'facture',
        prestations,
        simpleLines,
        client: clientObj,
        issuer: getIssuer(),
        dossierNumbers,
        meta: {
          number: inv.invoice_number,
          date: inv.issue_date || today,
          dueDate: inv.due_date,
          tvaRate: inv.tva_rate,
          docType: inv.doc_type || 'facture',
          vatRegime: inv.vat_regime || null,
        },
      });
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const markIssued = async (inv) => {
    await api.patch(`/invoices/${inv.id}/mark-issued`, { issue_date: today });
    load();
  };

  const markPaid = async (inv) => {
    const amount = window.prompt(`Montant payé pour la facture ${inv.invoice_number || '#' + inv.id} (total : ${fmtMoney(inv.amount_ttc)}) :`, inv.amount_ttc);
    if (amount === null) return;
    const method = window.prompt('Mode de paiement (virement, cheque, carte, carpa, espece) :', 'virement');
    await api.patch(`/invoices/${inv.id}/mark-paid`, { paid_amount: parseFloat(amount), payment_method: method });
    load();
  };

  // Plan d'apurement : helpers
  const addScheduleRow = () => {
    setForm({ ...form, schedule: [...form.schedule, { expected_date: '', expected_amount: '', paid_date: '', paid_amount: '', payment_method: '' }] });
  };
  const removeScheduleRow = (idx) => {
    setForm({ ...form, schedule: form.schedule.filter((_, i) => i !== idx) });
  };
  const updateScheduleRow = (idx, field, value) => {
    const newSched = [...form.schedule];
    newSched[idx] = { ...newSched[idx], [field]: value };
    setForm({ ...form, schedule: newSched });
  };

  // Calculs
  const ttc = (parseFloat(form.amount_ht) || 0) * (1 + (parseFloat(form.tva_rate) || 0) / 100);
  const scheduleTotal = form.schedule.reduce((sum, s) => sum + (parseFloat(s.expected_amount) || 0), 0);

  // Groupement
  const groupedInvoices = () => {
    if (groupBy === 'dossier') {
      const groups = {};
      filteredInvoices.forEach(inv => {
        const key = inv.dossier || '(Sans dossier)';
        if (!groups[key]) groups[key] = [];
        groups[key].push(inv);
      });
      return Object.keys(groups).sort((a, b) => {
        if (a === '(Sans dossier)') return 1;
        if (b === '(Sans dossier)') return -1;
        return a.localeCompare(b, 'fr');
      }).map(k => ({ label: k, emoji: '📁', items: groups[k] }));
    }
    if (groupBy === 'client') {
      const groups = {};
      filteredInvoices.forEach(inv => {
        const key = inv.client_name || '(Sans client)';
        if (!groups[key]) groups[key] = [];
        groups[key].push(inv);
      });
      return Object.keys(groups).sort((a, b) => {
        if (a === '(Sans client)') return 1;
        if (b === '(Sans client)') return -1;
        return a.localeCompare(b, 'fr');
      }).map(k => ({ label: k, emoji: '👥', items: groups[k] }));
    }
    return null;
  };
  const groups = groupedInvoices();

  return (
    <div>
      <div className="page-header page-header-row">
        <div>
          <h1>Facturation</h1>
          <p>{invoices.length} facture{invoices.length !== 1 ? 's' : ''} · Suivi émission et paiements</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ Nouvelle facture</button>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
        <div className="stat-card" style={{ textAlign: 'left' }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>📋 À émettre</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--text)' }}>{fmtMoney(stats.to_issue)}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>{stats.nb_to_issue} facture{stats.nb_to_issue !== '1' ? 's' : ''}</div>
        </div>
        <div className="stat-card" style={{ textAlign: 'left' }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>📨 Émises en attente</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--primary)' }}>{fmtMoney(stats.pending)}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>{stats.nb_pending} facture{stats.nb_pending !== '1' ? 's' : ''}</div>
        </div>
        <div className="stat-card" style={{ textAlign: 'left' }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>⚠️ En retard</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--danger)' }}>{fmtMoney(stats.overdue_total)}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>{stats.overdue_nb} facture{stats.overdue_nb !== '1' ? 's' : ''}</div>
        </div>
        <div className="stat-card" style={{ textAlign: 'left' }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>✅ Encaissé ce mois</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--success)' }}>{fmtMoney(stats.paid_this_month)}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>Année : {fmtMoney(stats.paid_this_year)}</div>
        </div>
      </div>

      {/* Onglets par statut */}
      <div className="task-filters">
        {STATUS_TABS.map(t => (
          <button key={t.value} className={`filter-btn ${activeTab === t.value ? 'active' : ''}`} onClick={() => setActiveTab(t.value)}>
            {t.emoji} {t.label}
          </button>
        ))}
      </div>

      {/* Groupement */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-light)' }}>Organiser :</label>
        <select className="form-input" style={{ maxWidth: 240, fontSize: '0.85rem', fontWeight: 600 }} value={groupBy} onChange={e => setGroupBy(e.target.value)}>
          <option value="status">Par statut / date</option>
          <option value="dossier">📁 Par dossier</option>
          <option value="client">👥 Par client</option>
        </select>
      </div>

      {/* Liste */}
      {filteredInvoices.length === 0 ? (
        <div className="empty-state">
          <div className="empty-emoji">💶</div>
          <p>Aucune facture. {activeTab === 'all' ? 'Créez votre première facture !' : 'Aucune facture dans cet onglet.'}</p>
        </div>
      ) : groups ? (
        <div>
          {groups.map(group => (
            <div key={group.label} style={{ marginBottom: 20 }}>
              <div className="day-section-title">
                <span className="section-emoji">{group.emoji}</span>
                {group.label}
                <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                  {fmtMoney(group.items.reduce((s, i) => s + parseFloat(i.amount_ttc || 0), 0))} · {group.items.length} fact.
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {group.items.map(inv => <InvoiceRow key={inv.id} inv={inv} onEdit={openEdit} onDelete={deleteInvoice} onMarkIssued={markIssued} onMarkPaid={markPaid} onDetail={generateDetail} today={today} />)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filteredInvoices.map(inv => <InvoiceRow key={inv.id} inv={inv} onEdit={openEdit} onDelete={deleteInvoice} onMarkIssued={markIssued} onMarkPaid={markPaid} onDetail={generateDetail} today={today} />)}
        </div>
      )}

      {/* Modale création/édition */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 700 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h2 style={{ margin: 0 }}>{editingId ? 'Modifier la facture' : 'Nouvelle facture'}</h2>
              <button type="button" className="btn-icon" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={save}>
              <div className="form-group">
                <label>Type de document</label>
                <select className="form-input" value={form.doc_type} onChange={e => setForm({ ...form, doc_type: e.target.value })}>
                  {Object.entries(DOC_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>N° facture (de Billit)</label>
                  <input className="form-input" value={form.invoice_number} onChange={e => setForm({ ...form, invoice_number: e.target.value })} placeholder="Ex : 2026-042" />
                </div>
                <div className="form-group">
                  <label>Statut</label>
                  <select className="form-input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                    <option value="brouillon">📋 À émettre</option>
                    <option value="emise">📨 Émise</option>
                    <option value="payee">✅ Payée</option>
                    <option value="annulee">🚫 Annulée</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Client</label>
                  <select className="form-input" value={form.client_id} onChange={e => handleClientChange(e.target.value)}>
                    <option value="">— Sélectionner —</option>
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.type === 'societe' ? '🏢' : '👤'} {c.name} ({c.country})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Dossier</label>
                  <input className="form-input" value={form.dossier} onChange={e => setForm({ ...form, dossier: e.target.value })} list="fac-dossiers" placeholder="Ex : Dupont c/ Martin" />
                  <datalist id="fac-dossiers">
                    {dossiers.map(d => <option key={d} value={d} />)}
                  </datalist>
                </div>
              </div>

              <div className="form-group">
                <label>Description de la prestation</label>
                <textarea className="form-input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} style={{ minHeight: 60 }} placeholder="Ex : Rédaction conclusions + plaidoirie audience du 15/05" />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Montant HT (€) *</label>
                  <input className="form-input" type="number" step="0.01" value={form.amount_ht} onChange={e => setForm({ ...form, amount_ht: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Régime de TVA</label>
                  <select className="form-input" value={form.vat_regime} onChange={e => handleRegimeChange(e.target.value)}>
                    {Object.entries(VAT_REGIMES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Montant TTC</label>
                  <input className="form-input" value={fmtMoney(ttc)} disabled style={{ fontWeight: 700, background: 'var(--primary-bg)', color: 'var(--primary)' }} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date d'émission</label>
                  <input className="form-input" type="date" value={form.issue_date} onChange={e => handleIssueDateChange(e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Date d'échéance (+ 8 jours auto)</label>
                  <input className="form-input" type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} />
                </div>
              </div>

              {/* Plan d'apurement */}
              <div style={{ padding: 12, background: 'var(--bg)', borderRadius: 10, marginBottom: 14, border: '1px solid var(--border)' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.has_payment_plan} onChange={e => setForm({ ...form, has_payment_plan: e.target.checked })} />
                  💳 Plan d'apurement (paiement en plusieurs échéances)
                </label>
                {form.has_payment_plan && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 6 }}>
                      Total programmé : {fmtMoney(scheduleTotal)} / TTC : {fmtMoney(ttc)}
                      {Math.abs(scheduleTotal - ttc) > 0.01 && <span style={{ color: 'var(--warning)', fontWeight: 700 }}> ⚠ écart : {fmtMoney(ttc - scheduleTotal)}</span>}
                    </div>
                    {form.schedule.map((s, i) => (
                      <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        <input type="date" className="form-input" style={{ flex: 1, minWidth: 130, fontSize: '0.82rem' }} value={s.expected_date} onChange={e => updateScheduleRow(i, 'expected_date', e.target.value)} />
                        <input type="number" step="0.01" className="form-input" style={{ flex: 1, minWidth: 100, fontSize: '0.82rem' }} placeholder="Montant €" value={s.expected_amount} onChange={e => updateScheduleRow(i, 'expected_amount', e.target.value)} />
                        <input type="date" className="form-input" style={{ flex: 1, minWidth: 130, fontSize: '0.82rem' }} placeholder="Payé le" value={s.paid_date || ''} onChange={e => updateScheduleRow(i, 'paid_date', e.target.value)} />
                        <input type="number" step="0.01" className="form-input" style={{ flex: 1, minWidth: 100, fontSize: '0.82rem' }} placeholder="Payé €" value={s.paid_amount || ''} onChange={e => updateScheduleRow(i, 'paid_amount', e.target.value)} />
                        <button type="button" className="btn-ghost" onClick={() => removeScheduleRow(i)}>🗑️</button>
                      </div>
                    ))}
                    <button type="button" className="btn btn-secondary btn-sm" onClick={addScheduleRow}>+ Ajouter une échéance</button>
                  </div>
                )}
              </div>

              {/* Paiement simple (si pas de plan) */}
              {!form.has_payment_plan && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Montant payé (€)</label>
                    <input className="form-input" type="number" step="0.01" value={form.paid_amount} onChange={e => setForm({ ...form, paid_amount: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Date de paiement</label>
                    <input className="form-input" type="date" value={form.paid_date} onChange={e => setForm({ ...form, paid_date: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Mode de paiement</label>
                    <select className="form-input" value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })}>
                      {PAYMENT_METHODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                </div>
              )}

              {/* CARPA */}
              <div style={{ padding: 12, background: 'var(--primary-bg)', borderRadius: 10, marginBottom: 14 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.carpa} onChange={e => setForm({ ...form, carpa: e.target.checked })} />
                  🏦 Passage par CARPA / compte tiers
                </label>
                {form.carpa && (
                  <div style={{ marginTop: 8 }}>
                    <label style={{ fontSize: '0.8rem' }}>Montant CARPA (€)</label>
                    <input className="form-input" type="number" step="0.01" value={form.carpa_amount} onChange={e => setForm({ ...form, carpa_amount: e.target.value })} placeholder="Peut être différent du montant total" />
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea className="form-input" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} style={{ minHeight: 60 }} />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">{editingId ? 'Enregistrer' : 'Créer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function InvoiceRow({ inv, onEdit, onDelete, onMarkIssued, onMarkPaid, onDetail, today }) {
  const isLate = inv.status === 'emise' && inv.due_date && inv.due_date < today;
  const reste = parseFloat(inv.amount_ttc || 0) - parseFloat(inv.paid_amount || 0);
  const statusBadge = {
    brouillon: { label: '📋 À émettre', color: '#64748b', bg: '#f1f5f9' },
    emise: { label: isLate ? '⚠️ En retard' : '📨 Émise', color: isLate ? '#ef4444' : '#3b82f6', bg: isLate ? '#fef2f2' : '#eff6ff' },
    payee: { label: '✅ Payée', color: '#10b981', bg: '#ecfdf5' },
    annulee: { label: '🚫 Annulée', color: '#94a3b8', bg: '#f8fafc' },
  }[inv.status];
  const docBadge = (DOC_TYPES[inv.doc_type] || {}).badge;

  return (
    <div className="task-item" style={{ cursor: 'pointer' }} onClick={() => onEdit(inv)}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{inv.invoice_number || '(sans numéro)'}</div>
          {docBadge && <span className="badge" style={{ background: docBadge.bg, color: docBadge.color }}>{docBadge.text}</span>}
          <span className="badge" style={{ background: statusBadge.bg, color: statusBadge.color }}>{statusBadge.label}</span>
          {inv.carpa && <span className="badge" style={{ background: '#eef2ff', color: '#6366f1' }}>🏦 CARPA</span>}
          {inv.has_payment_plan && <span className="badge" style={{ background: '#fff7ed', color: '#f97316' }}>💳 Plan</span>}
        </div>
        <div style={{ fontSize: '0.82rem', color: 'var(--text-light)', marginTop: 3 }}>
          {inv.client_name && <span>👥 {inv.client_name} · </span>}
          {inv.dossier && <span>📁 {inv.dossier} · </span>}
          {inv.issue_date && <span>Émise {fmtDate(inv.issue_date)}</span>}
          {inv.due_date && inv.status === 'emise' && <span> · Échéance {fmtDate(inv.due_date)}</span>}
        </div>
        {inv.description && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 3, fontStyle: 'italic' }}>{inv.description}</div>}
      </div>

      <div style={{ textAlign: 'right', minWidth: 120 }}>
        <div style={{ fontWeight: 700, fontSize: '1rem' }}>{fmtMoney(inv.amount_ttc)}</div>
        {parseFloat(inv.tva_rate) > 0 && <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>HT : {fmtMoney(inv.amount_ht)}</div>}
        {parseFloat(inv.tva_rate) === 0 && <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>TVA 0 % intracom</div>}
        {parseFloat(inv.paid_amount) > 0 && (
          <div style={{ fontSize: '0.72rem', color: reste > 0.01 ? 'var(--warning)' : 'var(--success)', fontWeight: 600 }}>
            Payé : {fmtMoney(inv.paid_amount)}{reste > 0.01 && ` · Reste ${fmtMoney(reste)}`}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 4, flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        {inv.status === 'brouillon' && (
          <button className="btn btn-primary btn-xs" onClick={() => onMarkIssued(inv)} title="Marquer comme émise">📨 Émettre</button>
        )}
        {inv.status === 'emise' && reste > 0.01 && (
          <button className="btn btn-primary btn-xs" onClick={() => onMarkPaid(inv)} title="Marquer comme payée">✅ Payée</button>
        )}
        <button className="btn-ghost" onClick={() => onDetail(inv)} title="Facture détaillée (PDF)">📄</button>
        <button className="btn-ghost" onClick={() => onDelete(inv.id)}>🗑️</button>
      </div>
    </div>
  );
}
